import { useCallback, useSyncExternalStore } from "react";

/** The non-standard event Chromium browsers fire when the app can be installed. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * - `prompt`: the browser offers a native install dialog (Chrome, Edge, Android).
 * - `manual`: iOS has no install API; the user goes through Share > Add to Home Screen.
 * - `null`: already installed, or the browser cannot install the app.
 */
export type InstallMode = "prompt" | "manual" | null;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let isListening = false;
const listeners = new Set<() => void>();

const setDeferredPrompt = (event: BeforeInstallPromptEvent | null) => {
  deferredPrompt = event;
  listeners.forEach((listener) => listener());
};

/**
 * Starts capturing the install prompt. Idempotent. Called from main.tsx because
 * the browser may fire the event before React mounts the components using it.
 */
export const listenForInstallPrompt = () => {
  if (isListening) return;
  isListening = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    setDeferredPrompt(event as BeforeInstallPromptEvent);
  });
  window.addEventListener("appinstalled", () => setDeferredPrompt(null));
};

const subscribe = (listener: () => void) => {
  listenForInstallPrompt();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => deferredPrompt;

/** True when the app runs from the home screen icon (installed PWA). */
export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** iPhone / iPod, and iPadOS which reports a desktop Safari user agent. */
export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);

const getInstallMode = (prompt: BeforeInstallPromptEvent | null) => {
  if (isStandalone()) return null;
  if (prompt) return "prompt";
  return isIos() ? "manual" : null;
};

export const useInstallPrompt = () => {
  const prompt = useSyncExternalStore(subscribe, getSnapshot, () => null);

  /** Opens the native install dialog. Resolves true when the user accepts. */
  const promptInstall = useCallback(async () => {
    if (!prompt) return false;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // A captured prompt can only be shown once
    setDeferredPrompt(null);
    return outcome === "accepted";
  }, [prompt]);

  return { installMode: getInstallMode(prompt) as InstallMode, promptInstall };
};
